import React, { useCallback } from 'react';

import {
  IncidentHospital,
  useIncidentHospitals,
  useCreateIncidentHospital,
  useEditIncidentHospital,
} from 'components/IAP/hooks/incidentHospitalsHooks';
import { HospitalBase } from 'types/iapBaseTypes';

import {
  MedicalPlanTable,
  type MedicalPlanColumn,
  type MedicalPlanDraft,
} from './MedicalPlanTable';
import { useRecordMedicalPlanVersion } from './medicalPlanVersionRecording';

const COLUMNS: MedicalPlanColumn[] = [
  {
    kind: 'text',
    field: 'hospital_name',
    header: 'Name',
    placeholder: 'Hospital Name *',
    maxLength: 200,
  },
  {
    kind: 'text',
    field: 'address',
    header: 'Address, Lat/Long if Helipad',
    placeholder: 'Address',
  },
  {
    kind: 'text',
    field: 'contact_number',
    header: 'Contact Information',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    kind: 'text',
    field: 'travel_time_air',
    header: 'Travel Air',
    placeholder: 'e.g. 15 min',
    maxLength: 50,
  },
  {
    kind: 'text',
    field: 'travel_time_ground',
    header: 'Travel Ground',
    placeholder: 'e.g. 30 min',
    maxLength: 50,
  },
  { kind: 'checkbox', field: 'trauma_center', header: 'Trauma Center' },
  {
    kind: 'text',
    field: 'trauma_center_level',
    header: 'Trauma Level',
    placeholder: 'e.g. Level I',
    maxLength: 20,
  },
  { kind: 'checkbox', field: 'burn_center', header: 'Burn Center' },
  { kind: 'checkbox', field: 'heli_pad', header: 'Helipad' },
];

const EMPTY_DRAFT: MedicalPlanDraft = {
  hospital_name: '',
  address: '',
  contact_number: '',
  travel_time_air: '',
  travel_time_ground: '',
  trauma_center: false,
  trauma_center_level: '',
  burn_center: false,
  heli_pad: false,
};

const toEditPayload = (
  entity: IncidentHospital,
  override: MedicalPlanDraft
): HospitalBase & { id: string; hospital_id: string; archived: boolean } => ({
  id: entity.id,
  hospital_id: entity.hospital_id,
  hospital_name: entity.hospital_name,
  description: entity.description,
  address: entity.address,
  contact_number: entity.contact_number,
  travel_time_air: entity.travel_time_air,
  travel_time_ground: entity.travel_time_ground,
  burn_center: entity.burn_center,
  heli_pad: entity.heli_pad,
  trauma_center: entity.trauma_center,
  trauma_center_level: entity.trauma_center_level,
  archived: entity.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const HospitalsTable: React.FC<Props> = ({ readOnly = false }) => {
  const { data: incidentHospitals } = useIncidentHospitals('incident');
  const activeIncidentHospitals = (incidentHospitals ?? []).filter(
    (e) => !e.archived
  );

  const recordVersion = useRecordMedicalPlanVersion();
  const { mutateAsync: createIncidentHospital, isPending: isCreating } =
    useCreateIncidentHospital();
  const { mutateAsync: editIncidentHospital, isPending: isEditing } =
    useEditIncidentHospital();

  const onCreate = useCallback(
    async (draft: MedicalPlanDraft) => {
      await createIncidentHospital({
        hospital_name: draft.hospital_name as string,
        address: draft.address as string,
        contact_number: draft.contact_number as string,
        travel_time_air: draft.travel_time_air as string,
        travel_time_ground: draft.travel_time_ground as string,
        trauma_center: draft.trauma_center as boolean,
        trauma_center_level: draft.trauma_center_level as string,
        burn_center: draft.burn_center as boolean,
        heli_pad: draft.heli_pad as boolean,
      });
      recordVersion();
    },
    [createIncidentHospital, recordVersion]
  );

  const onUpdate = useCallback(
    async (entity: IncidentHospital, draft: MedicalPlanDraft) => {
      await editIncidentHospital(toEditPayload(entity, draft));
      recordVersion();
    },
    [editIncidentHospital, recordVersion]
  );

  const onDelete = useCallback(
    async (entity: IncidentHospital) => {
      await editIncidentHospital(toEditPayload(entity, { archived: true }));
      recordVersion();
    },
    [editIncidentHospital, recordVersion]
  );

  return (
    <MedicalPlanTable
      testId="ics206-hospitals"
      entityNoun="Hospital"
      entityNounPlural="Hospitals"
      addButtonLabel="Add Hospital"
      noDataMessage="No hospitals defined."
      entities={activeIncidentHospitals}
      columns={COLUMNS}
      emptyDraft={EMPTY_DRAFT}
      requiredField="hospital_name"
      rowLabelField="hospital_name"
      isSaving={isCreating || isEditing}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
